<template>
    <b-modal
             :id="modalId"
             v-model="showModal"
             size="lg"
             centered
             scrollable
             no-fade
             no-enforce-focus
             :header-bg-variant="headerBgVariant"
             header-text-variant="white"
             title-tag="h4"
             title-class="align-self-center"
             hide-footer
             @hidden="onModalHidden">
        <template #modal-header-close>
            <b-icon icon="close" scale="0.6" />
        </template>
        <template #modal-title>{{ modalTitle }}</template>

        <template v-if="showModal">
            <!-- Original Request Information -->
            <div v-if="extractResult && extractResult.productQuery" class="original-request mb-4">
                <h6 class="text-muted mb-2">{{ originalRequestLabel }}</h6>
                <div class="card">
                    <div class="card-body">
                        <p class="mb-1"><strong>{{ productQueryLabel }}:</strong></p>
                        <p class="mb-0" v-if="extractResult.productIds && extractResult.productIds.length > 0">{{ $t('components.order.basket.basketitem.product_ids') }}: {{
                            extractResult.productIds.join(', ') }}</p>
                        <p class="mb-0">{{ extractResult.productQuery }}</p>
                        <p class="mb-0">{{ $t('components.order.basket.basketitem.quantity') }}: {{ extractResult.quantity }}</p>
                    </div>
                </div>
            </div>

            <!-- Product Type Tabs -->
            <div class="product-type-tabs mb-3">
                <b-nav tabs>
                    <b-nav-item :active="selectedProductType === 'Product'" @click="selectedProductType = 'Product'">
                        Products
                    </b-nav-item>
                    <b-nav-item :active="selectedProductType === 'MarketingProduct'" @click="selectedProductType = 'MarketingProduct'">
                        Marketing-Products
                    </b-nav-item>
                </b-nav>
            </div>

            <!-- Search Input -->
            <div class="search-section mb-4">
                <label for="product-search-input" class="form-label">{{ searchLabel }}</label>
                <div class="input-group">
                    <b-input
                             id="product-search-input"
                             v-model="searchQuery"
                             :placeholder="searchPlaceholder"
                             @keyup.enter="searchProducts"
                             @input="onSearchInput" />
                    <div class="input-group-append">
                        <b-button variant="primary" @click="searchProducts" :disabled="!searchQuery.trim()">
                            <b-icon icon="search"></b-icon>
                        </b-button>
                    </div>
                </div>
            </div>

            <!-- Search Results -->
            <div class="search-results" style="min-height: 300px; position: relative;">
                <h6 class="text-muted mb-3">{{ resultsLabel }}</h6>

                <!-- Loading Overlay -->
                <div v-if="loading" class="position-absolute w-100 h-100 d-flex align-items-center justify-content-center"
                     style="top: 0; left: 0; background: rgba(255, 255, 255, 0.8); z-index: 10;">
                    <div class="text-center">
                        <b-spinner variant="primary" label="Loading..."></b-spinner>
                        <p class="mt-2 text-muted">{{ loadingText }}</p>
                    </div>
                </div>

                <!-- No Results -->
                <div v-if="!loading && searchResults.length === 0 && searchQuery.trim()" class="text-center py-4">
                    <b-icon icon="search" class="text-muted" scale="2"></b-icon>
                    <p class="mt-2 text-muted">{{ noResultsText }}</p>
                </div>

                <!-- Results List -->
                <div v-else-if="searchResults.length > 0" class="list-group">
                    <div v-for="product in searchResults" :key="product.id"
                         class="list-group-item list-group-item-action d-flex justify-content-between align-items-center">
                        <div class="flex-grow-1 d-flex align-items-center">
                            <!-- Product Image -->
                            <div class="mr-3" style="width: 60px; height: 60px;">
                                <img v-if="product.image"
                                     :src="product.image"
                                     :alt="product.name"
                                     class="img-fluid rounded"
                                     style="width: 100%; height: 100%; object-fit: cover;" />
                                <div v-else class="bg-light rounded d-flex align-items-center justify-content-center"
                                     style="width: 100%; height: 100%;">
                                    <b-icon icon="images" class="text-muted"></b-icon>
                                </div>
                            </div>

                            <!-- Product Details -->
                            <div class="flex-grow-1">
                                <h6 class="mb-1">{{ product.name }}</h6>
                                <p v-if="product.pharmaCode" class="mb-0 text-muted">
                                    {{ $t('components.order.basket.basketitem.pharma_code') }}: {{ product.pharmaCode }}
                                </p>
                                <p v-if="product.articleNumber" class="mb-0 text-muted">
                                    {{ $t('components.order.basket.basketitem.article_number') }}: {{ product.articleNumber }}
                                </p>
                                <p v-else class="mb-0 text-muted">
                                    {{ $t('components.order.basket.basketitem.product_gtin') }}: {{ product.id }}
                                </p>
                                <p v-if="product.portionierung" class="mb-0 text-muted">
                                    {{ product.portionierung }}
                                </p>
                            </div>
                        </div>
                        <div class="ml-3">
                            <b-button variant="outline-primary"
                                      size="sm"
                                      @click="selectProduct(product)">
                                {{ selectButtonText }}
                            </b-button>
                        </div>
                    </div>
                </div>

                <!-- Initial State -->
                <div v-else-if="!loading" class="text-center py-4">
                    <b-icon icon="search" class="text-muted" scale="2"></b-icon>
                    <p class="mt-2 text-muted">{{ initialStateText }}</p>
                </div>
            </div>

            <div class="d-flex justify-content-end mt-4">
                <b-button variant="light" @click="closeModal">
                    {{ closeButtonText }}
                </b-button>
            </div>
        </template>
    </b-modal>
</template>

<script>
export default {
    name: 'ProductSearchModal',
    props: {
        // Modal configuration
        modalId: {
            type: String,
            required: true
        },
        show: {
            type: Boolean,
            default: false
        },
        headerBgVariant: {
            type: String,
            default: 'secondary'
        },
        initialProductType: {
            type: String,
            default: 'Product',
            validator: value => ['Product', 'MarketingProduct'].includes(value)
        },

        // Text customization
        modalTitle: {
            type: String,
            default: 'Search Products'
        },
        searchLabel: {
            type: String,
            default: 'Search Products'
        },
        searchPlaceholder: {
            type: String,
            default: 'Enter product name or ID...'
        },
        resultsLabel: {
            type: String,
            default: 'Search Results'
        },
        loadingText: {
            type: String,
            default: 'Searching products...'
        },
        noResultsText: {
            type: String,
            default: 'No products found'
        },
        initialStateText: {
            type: String,
            default: 'Start searching for products'
        },
        selectButtonText: {
            type: String,
            default: 'Select'
        },
        closeButtonText: {
            type: String,
            default: 'Close'
        },
        // New props for original request display
        extractResult: {
            type: Object,
            default: null
        },
        originalRequestLabel: {
            type: String,
            default: 'Original Request'
        },
        productQueryLabel: {
            type: String,
            default: 'Product Query'
        }
    },
    data() {
        return {
            searchQuery: '',
            searchResults: [],
            loading: false,
            selectedProductType: this.initialProductType
        }
    },
    computed: {
        showModal: {
            get() {
                return this.show
            },
            set(value) {
                this.$emit('update:show', value)
            }
        }
    },
    watch: {
        show(newVal) {
            if (newVal) {
                this.resetSearch()
                this.selectedProductType = this.initialProductType
            }
        },
        selectedProductType() {
            // Re-run search when product type changes if there's already a search query
            if (this.searchQuery.trim()) {
                this.searchProducts()
            }
        }
    },
    methods: {
        resetSearch() {
            this.searchQuery = ''
            this.searchResults = []
        },
        async searchProducts() {
            if (!this.searchQuery.trim()) {
                this.searchResults = []
                return
            }

            this.loading = true
            try {
                const storeModule = this.selectedProductType === 'MarketingProduct' ? 'marketingproducts' : 'products'
                const products = await this.$store.dispatch(`${storeModule}/searchByQuery`, {
                    query: this.searchQuery,
                    pageSize: 10
                })
                this.searchResults = products.map(item => item.value)
            } catch (error) {
                console.error('Error searching products:', error)
                this.searchResults = []
            } finally {
                this.loading = false
            }
        },
        onSearchInput() {
            if (this.searchQuery.length > 0) {
                this.searchProducts()
            } else {
                this.searchResults = []
            }
        },
        selectProduct(product) {
            // Close modal first to prevent focus issues
            this.closeModal()

            // Use nextTick to ensure modal is fully closed before emitting
            this.$nextTick(() => {
                this.$emit('product-selected', product, this.selectedProductType)
            })
        },
        closeModal() {
            this.showModal = false
        },
        onModalHidden() {
            this.resetSearch()
            this.$emit('modal-hidden')
        }
    }
}
</script>

<style lang="scss" scoped>
.search-section {
    .input-group {
        .form-control {
            border-right: 0;
        }

        .input-group-append {
            .btn {
                border-left: 0;
            }
        }
    }
}

.search-results {
    .list-group {
        max-height: 400px;
        overflow-y: auto;

        .list-group-item {
            transition: background-color 0.2s ease;

            &:hover {
                background-color: rgba(0, 123, 255, 0.05);
            }
        }
    }
}

.alternatives-list {
    .list-group {
        max-height: 600px;
        overflow-y: auto;

        .list-group-item {
            cursor: pointer;
        }
    }
}
</style>
